import React, { useState, useCallback, useRef, useEffect } from "react";
import ReactFlow, {
  addEdge,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  ReactFlowProvider,
  MarkerType,
} from "reactflow";
import "reactflow/dist/style.css";
import { v4 as uuidv4 } from "uuid";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { selectUserInfo } from "@redux/slices/authSlice";
import { parseError } from "../utils/utils";
import httpClient from "@services/httpClient";
import { generateGuid } from "@utils/utils";

import {
  PIPELINE_NODE_TYPES,
} from "@components/misc/Workflow/PipelineNodes";

// ─── Node palette config ──────────────────────────────────────────────────────
const NODE_PALETTE = [
  {
    type: "trigger",
    label: "Trigger",
    description: "Webhook / SNS / Schedule",
    color: "purple",
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
  },
  {
    type: "connector",
    label: "Connector",
    description: "Call a platform API",
    color: "blue",
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    type: "transformer",
    label: "Transformer",
    description: "Transform & map fields",
    color: "orange",
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    ),
  },
];

const COLOR_MAP = {
  purple: {
    pill: "bg-purple-100 text-purple-700 border border-purple-300",
    drag: "border-purple-400 hover:border-purple-600 hover:bg-purple-50",
  },
  blue: {
    pill: "bg-blue-100 text-blue-700 border border-blue-300",
    drag: "border-blue-400 hover:border-blue-600 hover:bg-blue-50",
  },
  yellow: {
    pill: "bg-yellow-100 text-yellow-700 border border-yellow-300",
    drag: "border-yellow-400 hover:border-yellow-600 hover:bg-yellow-50",
  },
  green: {
    pill: "bg-green-100 text-green-700 border border-green-300",
    drag: "border-green-400 hover:border-green-600 hover:bg-green-50",
  },
  orange: {
    pill: "bg-orange-100 text-orange-700 border border-orange-300",
    drag: "border-orange-400 hover:border-orange-600 hover:bg-orange-50",
  },
};

// ─── Template presets — all 4 data-flow patterns ─────────────────────────────
//
//  Broadcast 1:N
//    Pattern A — trigger = source  →  trigger fires AND is the data source
//    Pattern B — trigger + source  →  separate trigger (e.g. SNS) + source (e.g. S3)
//
//  Aggregate N:1
//    Pattern A — trigger = source per leg  →  each trigger also provides the data
//    Pattern B — trigger + source per leg  →  each leg has its own trigger + connector
// ─────────────────────────────────────────────────────────────────────────────
const TEMPLATES = {

  // ── Broadcast / 1:N ────────────────────────────────────────────────────

  fan_out: {
    label: "Broadcast — Pattern A",
    description: "Trigger is also the data source. Sends to multiple destinations in parallel.",
    nodes: [
      { id: "t1", type: "trigger", position: { x: 300, y: 50 }, data: { label: "Trigger / Source" } },
      { id: "c1", type: "connector", position: { x: 100, y: 240 }, data: { label: "Destination 1" } },
      { id: "c2", type: "connector", position: { x: 300, y: 240 }, data: { label: "Destination 2" } },
      { id: "c3", type: "connector", position: { x: 500, y: 240 }, data: { label: "Destination 3" } },
    ],
    edges: [
      { id: "e1", source: "t1", target: "c1", animated: false, style: { stroke: '#23346e', strokeWidth: 1.5 }, markerEnd: { type: MarkerType.ArrowClosed, color: '#23346e' } },
      { id: "e2", source: "t1", target: "c2", animated: false, style: { stroke: '#23346e', strokeWidth: 1.5 }, markerEnd: { type: MarkerType.ArrowClosed, color: '#23346e' } },
      { id: "e3", source: "t1", target: "c3", animated: false, style: { stroke: '#23346e', strokeWidth: 1.5 }, markerEnd: { type: MarkerType.ArrowClosed, color: '#23346e' } },
    ],
  },

  fan_out_b: {
    label: "Broadcast — Pattern B",
    description: "Separate trigger and source. Source reads data then broadcasts to multiple destinations.",
    nodes: [
      { id: "t1", type: "trigger", position: { x: 300, y: 40 }, data: { label: "Trigger" } },
      { id: "s1", type: "connector", position: { x: 300, y: 180 }, data: { label: "Source" } },
      { id: "c1", type: "connector", position: { x: 100, y: 340 }, data: { label: "Destination 1" } },
      { id: "c2", type: "connector", position: { x: 500, y: 340 }, data: { label: "Destination 2" } },
    ],
    edges: [
      { id: "e1", source: "t1", target: "s1", animated: false, style: { stroke: '#23346e', strokeWidth: 1.5 }, markerEnd: { type: MarkerType.ArrowClosed, color: '#23346e' } },
      { id: "e2", source: "s1", target: "c1", animated: false, style: { stroke: '#23346e', strokeWidth: 1.5 }, markerEnd: { type: MarkerType.ArrowClosed, color: '#23346e' } },
      { id: "e3", source: "s1", target: "c2", animated: false, style: { stroke: '#23346e', strokeWidth: 1.5 }, markerEnd: { type: MarkerType.ArrowClosed, color: '#23346e' } },
    ],
  },

  // ── Aggregate / N:1 ────────────────────────────────────────────────────

  fan_in: {
    label: "Aggregate — Pattern A",
    description: "Each trigger is also its own data source. All feeds merge into one destination.",
    nodes: [
      { id: "t1", type: "trigger", position: { x: 100, y: 50 }, data: { label: "Trigger 1" } },
      { id: "t2", type: "trigger", position: { x: 300, y: 50 }, data: { label: "Trigger 2" } },
      { id: "t3", type: "trigger", position: { x: 500, y: 50 }, data: { label: "Trigger 3" } },
      { id: "d1", type: "connector", position: { x: 300, y: 250 }, data: { label: "Destination" } },
    ],
    edges: [
      { id: "e1", source: "t1", target: "d1", animated: false, style: { stroke: '#23346e', strokeWidth: 1.5 }, markerEnd: { type: MarkerType.ArrowClosed, color: '#23346e' } },
      { id: "e2", source: "t2", target: "d1", animated: false, style: { stroke: '#23346e', strokeWidth: 1.5 }, markerEnd: { type: MarkerType.ArrowClosed, color: '#23346e' } },
      { id: "e3", source: "t3", target: "d1", animated: false, style: { stroke: '#23346e', strokeWidth: 1.5 }, markerEnd: { type: MarkerType.ArrowClosed, color: '#23346e' } },
    ],
  },

  fan_in_b: {
    label: "Aggregate — Pattern B",
    description: "Each source has a separate trigger and data connector. All merge into one destination.",
    nodes: [
      { id: "t1", type: "trigger", position: { x: 100, y: 40 }, data: { label: "Trigger 1" } },
      { id: "s1", type: "connector", position: { x: 100, y: 180 }, data: { label: "Source 1" } },
      { id: "t2", type: "trigger", position: { x: 420, y: 40 }, data: { label: "Trigger 2" } },
      { id: "s2", type: "connector", position: { x: 420, y: 180 }, data: { label: "Source 2" } },
      { id: "d1", type: "connector", position: { x: 260, y: 360 }, data: { label: "Destination" } },
    ],
    edges: [
      { id: "e1", source: "t1", target: "s1", animated: false, style: { stroke: '#23346e', strokeWidth: 1.5 }, markerEnd: { type: MarkerType.ArrowClosed, color: '#23346e' } },
      { id: "e2", source: "t2", target: "s2", animated: false, style: { stroke: '#23346e', strokeWidth: 1.5 }, markerEnd: { type: MarkerType.ArrowClosed, color: '#23346e' } },
      { id: "e3", source: "s1", target: "d1", animated: false, style: { stroke: '#23346e', strokeWidth: 1.5 }, markerEnd: { type: MarkerType.ArrowClosed, color: '#23346e' } },
      { id: "e4", source: "s2", target: "d1", animated: false, style: { stroke: '#23346e', strokeWidth: 1.5 }, markerEnd: { type: MarkerType.ArrowClosed, color: '#23346e' } },
    ],
  },

  // ── Sequential / A→B→C ─────────────────────────────────────────────────

  sequential: {
    label: "Sequential — 3-Step Chain",
    description: "Trigger fires, data passes through a middle step, then lands at the destination.",
    nodes: [
      { id: "n1", type: "trigger",   position: { x: 260, y: 40  }, data: { label: "Step 1 (Trigger)" } },
      { id: "n2", type: "connector", position: { x: 260, y: 200 }, data: { label: "Step 2" } },
      { id: "n3", type: "connector", position: { x: 260, y: 360 }, data: { label: "Step 3 (Destination)" } },
    ],
    edges: [
      { id: "e1", source: "n1", target: "n2", animated: false, style: { stroke: '#4f46e5', strokeWidth: 1.5 }, markerEnd: { type: MarkerType.ArrowClosed, color: '#4f46e5' } },
      { id: "e2", source: "n2", target: "n3", animated: false, style: { stroke: '#4f46e5', strokeWidth: 1.5 }, markerEnd: { type: MarkerType.ArrowClosed, color: '#4f46e5' } },
    ],
  },

  sequential_4: {
    label: "Sequential — 4-Step Chain",
    description: "A four-step linear pipeline: Trigger → Step 2 → Step 3 → Destination.",
    nodes: [
      { id: "n1", type: "trigger",   position: { x: 260, y: 40  }, data: { label: "Step 1 (Trigger)" } },
      { id: "n2", type: "connector", position: { x: 260, y: 190 }, data: { label: "Step 2" } },
      { id: "n3", type: "connector", position: { x: 260, y: 340 }, data: { label: "Step 3" } },
      { id: "n4", type: "connector", position: { x: 260, y: 490 }, data: { label: "Step 4 (Destination)" } },
    ],
    edges: [
      { id: "e1", source: "n1", target: "n2", animated: false, style: { stroke: '#4f46e5', strokeWidth: 1.5 }, markerEnd: { type: MarkerType.ArrowClosed, color: '#4f46e5' } },
      { id: "e2", source: "n2", target: "n3", animated: false, style: { stroke: '#4f46e5', strokeWidth: 1.5 }, markerEnd: { type: MarkerType.ArrowClosed, color: '#4f46e5' } },
      { id: "e3", source: "n3", target: "n4", animated: false, style: { stroke: '#4f46e5', strokeWidth: 1.5 }, markerEnd: { type: MarkerType.ArrowClosed, color: '#4f46e5' } },
    ],
  },
};

// ─── Platform field schemas (mirrors backend PLATFORM_CREDENTIAL_SCHEMA) ──────
// Each platform lists the exact fields needed to create an integration.
// Used by NodeConfigPanel to render the correct form dynamically.
const PLATFORM_FIELDS = {
  trigger: {
    platforms: [
      { value: "HTTP", label: "HTTP", triggerType: "Http" },
      { value: "Webhook", label: "Webhook", triggerType: "Http" },
      { value: "HubSpot", label: "HubSpot", triggerType: "HUBSPOT" },
      { value: "Salesforce Platform Event", label: "Salesforce Platform Event", triggerType: "SALESFORCE" },
      { value: "AWS SNS", label: "AWS SNS", triggerType: "SNS" },
      { value: "AWS SQS", label: "AWS SQS", triggerType: "SQS" },
      { value: "Azure Service Bus", label: "Azure Service Bus", triggerType: "AZURE_SERVICE_BUS" },
      { value: "WebSocket", label: "WebSocket", triggerType: "WEBSOCKET" },
      { value: "Time Trigger", label: "Time Trigger", triggerType: "Scheduled" },
      // New Triggers (Coming Soon)
      { value: "GraphQL Subscription", label: "GraphQL Subscription", triggerType: "GRAPHQL" },
      { value: "gRPC Trigger", label: "gRPC Trigger", triggerType: "GRPC" },
      { value: "Google Pub/Sub Trigger", label: "Google Pub/Sub Trigger", triggerType: "GCP_PUBSUB" },
      { value: "Kafka Trigger", label: "Kafka Trigger", triggerType: "KAFKA" },
      { value: "Redis Pub/Sub Trigger", label: "Redis Pub/Sub Trigger", triggerType: "REDIS" },
      { value: "Azure Event Grid Trigger", label: "Azure Event Grid Trigger", triggerType: "AZURE_EVENT_GRID" },
      { value: "Blob Storage Trigger", label: "Blob Storage Trigger", triggerType: "AZURE_BLOB" },
      { value: "Shopify Webhook", label: "Shopify Webhook", triggerType: "SHOPIFY" },
      { value: "Stripe Webhook", label: "Stripe Webhook", triggerType: "STRIPE" },
      { value: "Slack Event Trigger", label: "Slack Event Trigger", triggerType: "SLACK" },
    ],
    fields: {
      HTTP: [
        { key: "httpMethod", label: "Http Method", required: true, type: "select", options: ["GET", "POST", "PUT", "PATCH", "DELETE"] },
        { key: "signatureHeaderKey", label: "Request Signature Header Key", required: false },
        { key: "clientSecret", label: "Client Secret", required: false },
        { key: "requestPayloadSchema", label: "Request Payload Schema", required: false, type: "textarea" },
        { key: "responseSchema", label: "Response Schema", required: false, type: "textarea" },
      ],
      Webhook: [
        { key: "httpMethod", label: "Http Method", required: true, type: "select", options: ["GET", "POST", "PUT", "PATCH", "DELETE"] },
        { key: "signatureHeaderKey", label: "Request Signature Header Key", required: false },
        { key: "clientSecret", label: "Client Secret", required: false },
        { key: "requestPayloadSchema", label: "Request Payload Schema", required: false, type: "textarea" },
      ],
      HubSpot: [
        { key: "requestPayloadSchema", label: "Request Payload Schema", required: false, type: "textarea" },
      ],
      "Salesforce Platform Event": [
        { key: "platformEventTopicName", label: "Platform Event Topic Name", required: true },
        { key: "requestSchema", label: "Request Schema", required: false, type: "textarea" },
      ],
      "AWS SNS": [
        { key: "awsSnsTopicArn", label: "AWS SNS Topic ARN", required: true },
        { key: "requestSchema", label: "Request Schema", required: false, type: "textarea" },
      ],
      "AWS SQS": [
        { key: "awsSqsQueueUrl", label: "AWS SQS Queue URL", required: true },
        { key: "requestSchema", label: "Request Schema", required: false, type: "textarea" },
      ],
      "Azure Service Bus": [
        { key: "azureServiceBusQueueName", label: "Azure Service Bus Queue Name", required: true },
        { key: "requestSchema", label: "Request Schema", required: false, type: "textarea" },
      ],
      WebSocket: [
      ],
      "Time Trigger": [
        { key: "cronExpression", label: "Cron Expression", required: true, hint: "e.g. 0 9 * * MON-FRI" },
      ],
      "GraphQL Subscription": [],
      "gRPC Trigger": [],
      "Google Pub/Sub Trigger": [],
      "Kafka Trigger": [],
      "Redis Pub/Sub Trigger": [],
      "Azure Event Grid Trigger": [],
      "Blob Storage Trigger": [],
      "Shopify Webhook": [],
      "Stripe Webhook": [],
      "Slack Event Trigger": [],
    },
  },
  connector: {
    platforms: [
      { value: "Salesforce", label: "Salesforce" },
      { value: "HubSpot", label: "HubSpot" },
      { value: "Snowflake", label: "Snowflake" },
      { value: "AWS S3", label: "AWS S3" },
      { value: "AWS SNS", label: "AWS SNS" },
      { value: "AWS SQS", label: "AWS SQS" },
      { value: "REST API", label: "REST API" },
      { value: "Zoho CRM", label: "Zoho CRM" },
      { value: "Shopify", label: "Shopify" },
      { value: "Google BigQuery", label: "Google BigQuery" },
    ],
    objectTypes: {
      Salesforce: ["Lead", "Contact", "Account", "Opportunity", "Case"],
      HubSpot: ["contact", "deal", "company", "ticket"],
      Snowflake: ["table"],
      "AWS S3": ["file", "csv", "json"],
      "AWS SNS": ["notification"],
      "AWS SQS": ["message"],
      "REST API": ["any"],
      "Zoho CRM": ["Lead", "Contact", "Account", "Deal"],
      "Shopify": ["Order", "Product", "Customer"],
      "Google BigQuery": ["table"],
    },
    actions: {
      Salesforce: ["create", "update", "upsert"],
      HubSpot: ["create", "update", "upsert"],
      Snowflake: ["insert"],
      "AWS S3": ["upload"],
      "AWS SNS": ["publish"],
      "AWS SQS": ["send"],
      "REST API": ["POST", "GET", "PATCH", "PUT", "DELETE"],
      "Zoho CRM": ["create", "update", "upsert"],
      "Shopify": ["create", "update"],
      "Google BigQuery": ["insert"],
    },
    fields: {
      "REST API": [
        { key: "sourceUrl", label: "Source Url", required: true },
        { key: "httpMethod", label: "Http Method", required: true, type: "select", options: ["GET", "POST", "PUT", "PATCH", "DELETE"] },
        { key: "headers", label: "Headers", required: false, type: "headers" },
        { key: "requestPayloadSchema", label: "Request Payload Schema", required: false, type: "textarea" },
        { key: "responseSchema", label: "Response Schema", required: false, type: "textarea" },
      ],
      Salesforce: [
        { key: "httpMethod", label: "Http Method", required: true, type: "select", options: ["GET", "POST", "PUT", "PATCH", "DELETE"] },
        { key: "headers", label: "Headers", required: false, type: "headers" },
        { key: "requestPayloadSchema", label: "Request Payload Schema", required: false, type: "textarea" },
        { key: "responseSchema", label: "Response Schema", required: false, type: "textarea" },
      ],
      HubSpot: [
        { key: "httpMethod", label: "Http Method", required: true, type: "select", options: ["GET", "POST", "PUT", "PATCH", "DELETE"] },
        { key: "headers", label: "Headers", required: false, type: "headers" },
      ],
      "AWS S3": [
        { key: "s3ObjectKey", label: "Target Object Key", required: true },
      ],
      "AWS SNS": [],
      "AWS SQS": [],
      Snowflake: [
        { key: "table", label: "Target Table Name", required: true },
      ],
      "Zoho CRM": [],
      "Shopify": [],
      "Google BigQuery": [
        { key: "dataset", label: "Dataset ID", required: true },
        { key: "table", label: "Table ID", required: true },
      ],
    },
  },
};

// ─── Platform name → Connection platform value map ────────────────────────────
// Maps NodeConfigPanel platform values to the canonical platform strings stored
// in the Connection model (mirrors ConnectionsList normalizePlatform in reverse).
const PLATFORM_TO_CONNECTION = {
  // Trigger platforms
  "HubSpot": "HUBSPOT",
  "Salesforce Platform Event": "SALESFORCE",
  "AWS SNS": "SNS",
  "AWS SQS": "SQS",
  "Azure Service Bus": "AZURE_SB",
  // Connector platforms
  "Salesforce": "SALESFORCE",
  "Snowflake": "SNOWFLAKE",
  "AWS S3": "AWS_S3",
  "REST API": "REST_API",
  "Zoho CRM": "ZOHO_CRM",
  "Shopify": "SHOPIFY",
  "Google BigQuery": "GOOGLE_BIGQUERY",
};

// ─── Node config panel ────────────────────────────────────────────────────────
const NodeConfigPanel = ({ node, onChange, onClose, onDelete }) => {
  const [savedConnections, setSavedConnections] = React.useState([]);
  const [isPlatformDropdownOpen, setIsPlatformDropdownOpen] = React.useState(false);

  const nodeType = node ? node.type : "";
  const selectedPlatform = node ? (node.data.platform || "") : "";

  // Fetch saved connections whenever the selected platform changes
  React.useEffect(() => {
    if (!node) return;
    const connectionPlatform = PLATFORM_TO_CONNECTION[selectedPlatform];
    if (!connectionPlatform) {
      setSavedConnections([]);
      return;
    }
    const connType = nodeType === "trigger" ? "Trigger" : "Connector";
    httpClient
      .get(`/api/integrations/connections/?platform=${encodeURIComponent(connectionPlatform)}`)
      .then((res) => {
        const all = Array.isArray(res) ? res : (res?.data || []);
        // Include connections matching this node's type, or with no type set, or "Trigger" type connections
        // (most connections are stored as Trigger by default regardless of use)
        const filtered = all.filter(
          (c) => !c.connection_type || c.connection_type === connType || c.connection_type === "Trigger"
        );
        setSavedConnections(filtered);
        // Auto-select first account if none already chosen
        if (filtered.length >= 1 && !node.data.connection_id) {
          onChange({ ...node, data: { ...node.data, connection_id: filtered[0].connection_id } });
        }
      })
      .catch(() => setSavedConnections([]));
  }, [selectedPlatform, nodeType, node]);

  if (!node) return null;

  const update = (field, value) => onChange({ ...node, data: { ...node.data, [field]: value } });
  const updateConfig = (key, val) => update("config", { ...(node.data.config || {}), [key]: val });

  // Header helpers (for REST API / Salesforce connectors)
  const updateHeader = (idx, field, val) => {
    const headers = [...(node.data.config?.headers || [])];
    headers[idx] = { ...headers[idx], [field]: val };
    updateConfig("headers", headers);
  };
  const addHeader = () => updateConfig("headers", [...(node.data.config?.headers || []), { key: "", value: "" }]);
  const removeHeader = (idx) => {
    const headers = (node.data.config?.headers || []).filter((_, i) => i !== idx);
    updateConfig("headers", headers);
  };

  const schema = PLATFORM_FIELDS[nodeType === "connector" ? "connector" : "trigger"];

  // When platform changes, reset dependent fields
  const handlePlatformChange = (val) => {
    const platforms = schema?.platforms || [];
    const found = platforms.find((p) => p.value === val);
    onChange({
      ...node,
      data: {
        ...node.data,
        platform: val,
        triggerType: found?.triggerType || node.data.triggerType || "",
        objectType: "",
        action: "",
        connection_id: "",
        config: {},
      },
    });
  };

  const rawFields = schema?.fields?.[selectedPlatform] || [];
  const configFields = rawFields.filter(f => !["requestPayloadSchema", "responseSchema", "requestSchema"].includes(f.key));

  const SAMPLE_SCHEMA = "{\n  \"type\": \"object\",\n  \"properties\": {\n    \"id\": {\"type\": \"string\"},\n    \"status\": {\"type\": \"string\"}\n  }\n}";

  const inputClass =
    "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 bg-white";
  const labelClass = "block text-xs font-bold text-gray-600 mb-1";

  return (
    <div className="fixed lg:absolute right-0 top-[72px] lg:top-0 h-[calc(100%-72px)] lg:h-full w-full sm:w-80 bg-white border-l border-gray-200 shadow-xl z-[50] overflow-y-auto flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
        <h3 className="font-bold text-gray-800 capitalize">{node.type} Settings</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onDelete(node.id)}
            title="Delete node"
            className="rounded-lg px-2 py-1 text-xs font-bold text-red-600 border border-red-200 hover:bg-red-50 transition-all"
          >
            Delete
          </button>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold leading-none">×</button>
        </div>
      </div>

      <div className="p-4 space-y-4 flex-1">
        {/* Label */}
        <div>
          <label className={labelClass}>Label</label>
          <input type="text" value={node.data.label || ""} onChange={(e) => update("label", e.target.value)} className={inputClass} />
        </div>

        {/* ── TRIGGER fields ──────────────────────────────────────────── */}
        {nodeType === "trigger" && (
          <>
            <div className="relative">
              <label className={labelClass}>Platform</label>
              <div 
                className={`${inputClass} flex justify-between items-center cursor-pointer`}
                onClick={() => setIsPlatformDropdownOpen(!isPlatformDropdownOpen)}
              >
                <span className="truncate">
                  {selectedPlatform ? PLATFORM_FIELDS.trigger.platforms.find(p => p.value === selectedPlatform)?.label || "Select platform..." : "Select platform..."}
                </span>
                <svg className={`h-4 w-4 text-gray-500 transition-transform ${isPlatformDropdownOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              {isPlatformDropdownOpen && (
                <div className="absolute left-0 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-xl z-50 max-h-[320px] overflow-y-auto">
                  {PLATFORM_FIELDS.trigger.platforms.map((p) => (
                    <div 
                      key={p.value} 
                      className={`px-3 py-2.5 text-sm cursor-pointer hover:bg-blue-50 hover:text-blue-600 transition-colors ${selectedPlatform === p.value ? 'bg-blue-100 text-blue-700 font-bold' : 'text-gray-700'}`}
                      onClick={() => {
                        handlePlatformChange(p.value);
                        setIsPlatformDropdownOpen(false);
                      }}
                    >
                      {p.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {savedConnections.length > 1 && (
              <div>
                <label className={labelClass}>{selectedPlatform} Account</label>
                <select
                  value={node.data.connection_id || ""}
                  onChange={(e) => update("connection_id", e.target.value)}
                  className={inputClass}
                >
                  {savedConnections.map((c) => (
                    <option key={c.connection_id} value={c.connection_id}>
                      {c.display_name || c.connection_alias || c.account_identifier || c.platform}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </>
        )}

        {/* ── CONNECTOR fields ─────────────────────────────────────────── */}
        {nodeType === "connector" && (
          <>
            <div className="relative">
              <label className={labelClass}>Platform</label>
              <div 
                className={`${inputClass} flex justify-between items-center cursor-pointer`}
                onClick={() => setIsPlatformDropdownOpen(!isPlatformDropdownOpen)}
              >
                <span className="truncate">
                  {selectedPlatform ? PLATFORM_FIELDS.connector.platforms.find(p => p.value === selectedPlatform)?.label || "Select platform..." : "Select platform..."}
                </span>
                <svg className={`h-4 w-4 text-gray-500 transition-transform ${isPlatformDropdownOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              {isPlatformDropdownOpen && (
                <div className="absolute left-0 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-xl z-50 max-h-[320px] overflow-y-auto">
                  {PLATFORM_FIELDS.connector.platforms.map((p) => (
                    <div 
                      key={p.value} 
                      className={`px-3 py-2.5 text-sm cursor-pointer hover:bg-blue-50 hover:text-blue-600 transition-colors ${selectedPlatform === p.value ? 'bg-blue-100 text-blue-700 font-bold' : 'text-gray-700'}`}
                      onClick={() => {
                        handlePlatformChange(p.value);
                        setIsPlatformDropdownOpen(false);
                      }}
                    >
                      {p.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {savedConnections.length > 1 && (
              <div>
                <label className={labelClass}>{selectedPlatform} Account</label>
                <select
                  value={node.data.connection_id || ""}
                  onChange={(e) => update("connection_id", e.target.value)}
                  className={inputClass}
                >
                  {savedConnections.map((c) => (
                    <option key={c.connection_id} value={c.connection_id}>
                      {c.display_name || c.connection_alias || c.account_identifier || c.platform}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </>
        )}

        {/* ── Integration detail fields (trigger + connector) ───────────── */}
        {configFields.length > 0 && (
          <div className="border-t border-gray-100 pt-4 space-y-3">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Integration Details</p>
            {configFields.map((field) => (
              <div key={field.key}>
                <label className={labelClass}>
                  {field.label}
                  {field.required && <span className="text-red-500 ml-1">*</span>}
                </label>
                {field.type === "select" ? (
                  <select
                    value={node.data.config?.[field.key] || ""}
                    onChange={(e) => updateConfig(field.key, e.target.value)}
                    className={inputClass}
                  >
                    <option value="">Select...</option>
                    {(field.options || []).map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                ) : field.type === "textarea" ? (
                  <textarea
                    rows={3}
                    value={node.data.config?.[field.key] || ""}
                    onChange={(e) => updateConfig(field.key, e.target.value)}
                    placeholder={field.hint || ""}
                    className={inputClass}
                  />
                ) : field.type === "headers" ? (
                  <div className="space-y-2">
                    {(node.data.config?.headers || []).length === 0 && (
                      <p className="text-[11px] text-gray-400 italic">No headers added</p>
                    )}
                    {(node.data.config?.headers || []).map((h, idx) => (
                      <div key={idx} className="flex gap-2 items-center">
                        <input
                          type="text"
                          placeholder="Header Key"
                          value={h.key || ""}
                          onChange={(e) => updateHeader(idx, "key", e.target.value)}
                          className="flex-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-blue-500 bg-white"
                        />
                        <input
                          type="text"
                          placeholder="Header Value"
                          value={h.value || ""}
                          onChange={(e) => updateHeader(idx, "value", e.target.value)}
                          className="flex-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-blue-500 bg-white"
                        />
                        <button
                          onClick={() => removeHeader(idx)}
                          className="text-red-400 hover:text-red-600 text-lg leading-none font-bold"
                          title="Remove"
                        >×</button>
                      </div>
                    ))}
                    <button
                      onClick={addHeader}
                      className="mt-1 text-xs font-bold text-blue-600 hover:underline"
                    >+ Add Header</button>
                  </div>
                ) : (
                  <input
                    type="text"
                    value={node.data.config?.[field.key] || ""}
                    onChange={(e) => updateConfig(field.key, e.target.value)}
                    placeholder={field.hint || ""}
                    className={inputClass}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        {/* ── GENERIC SCHEMA fields (Trigger & Connector) ──────────────── */}
        {['trigger', 'connector'].includes(nodeType) && selectedPlatform && (
          <div className="border-t border-gray-100 pt-4 space-y-4">
            <div>
              <div className="flex justify-between items-end mb-1">
                <label className={labelClass} style={{ marginBottom: 0 }}>Request Payload Schema</label>
                <button onClick={() => updateConfig("requestPayloadSchema", SAMPLE_SCHEMA)} className="text-[10px] font-bold text-blue-500 hover:text-blue-700 transition">Sample Schema</button>
              </div>
              <textarea
                rows={3}
                value={node.data.config?.requestPayloadSchema || ""}
                onChange={(e) => updateConfig("requestPayloadSchema", e.target.value)}
                placeholder="Optional JSON mapping schema..."
                className={inputClass}
                style={{ fontFamily: "monospace", fontSize: "11px" }}
              />
            </div>
            <div>
              <div className="flex justify-between items-end mb-1">
                <label className={labelClass} style={{ marginBottom: 0 }}>Response Schema</label>
                <button onClick={() => updateConfig("responseSchema", SAMPLE_SCHEMA)} className="text-[10px] font-bold text-blue-500 hover:text-blue-700 transition">Sample Schema</button>
              </div>
              <textarea
                rows={3}
                value={node.data.config?.responseSchema || ""}
                onChange={(e) => updateConfig("responseSchema", e.target.value)}
                placeholder="Optional JSON mapping schema..."
                className={inputClass}
                style={{ fontFamily: "monospace", fontSize: "11px" }}
              />
            </div>
          </div>
        )}

        {/* ── AGGREGATOR fields ─────────────────────────────────────────── */}
        {nodeType === "aggregator" && (
          <div>
            <label className={labelClass}>Merge Strategy</label>
            <select value={node.data.strategy || "merge"} onChange={(e) => update("strategy", e.target.value)} className={inputClass}>
              <option value="merge">Deep Merge</option>
              <option value="first">First Wins</option>
              <option value="collect">Collect as List</option>
              <option value="concat">Concatenate Lists</option>
            </select>
          </div>
        )}

        {/* ── TRANSFORMER fields ─────────────────────────────────────────── */}
        {nodeType === "transformer" && (() => {
          const TRANSFORM_OPTIONS = [
            "Uppercase",
            "Lowercase",
            "None",
          ];
          const rules = node.data.transformRules || [];
          const updateRule = (idx, field, val) => {
            const updated = [...rules];
            updated[idx] = { ...updated[idx], [field]: val };
            update("transformRules", updated);
          };
          const addRule = () => update("transformRules", [...rules, { field: "", targetField: "", transformation: "" }]);
          const removeRule = (idx) => update("transformRules", rules.filter((_, i) => i !== idx));

          return (
            <div className="border-t border-gray-100 pt-4 space-y-3">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Mapping & Transformation Rules</p>
              {rules.length === 0 && (
                <p className="text-[11px] text-gray-400 italic">No rules added</p>
              )}
              {rules.map((rule, idx) => (
                <div key={idx} className="flex flex-col gap-1.5 bg-gray-50 p-2.5 rounded-lg border border-gray-200 relative">
                  <button
                    onClick={() => removeRule(idx)}
                    className="absolute top-2 right-2 text-red-400 hover:text-red-600 text-lg leading-none font-bold"
                    title="Remove"
                  >×</button>
                  <div className="pr-4">
                    <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Source Field Name</label>
                    <input
                      type="text"
                      placeholder="e.g. email_address"
                      value={rule.field || ""}
                      onChange={(e) => updateRule(idx, "field", e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:border-blue-500 bg-white mb-2"
                    />
                    <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Rename To (Target Field)</label>
                    <input
                      type="text"
                      placeholder="e.g. contactEmail"
                      value={rule.targetField || ""}
                      onChange={(e) => updateRule(idx, "targetField", e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:border-blue-500 bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-gray-500 uppercase mt-1 mb-1">Format Action</label>
                    <select
                      value={rule.transformation || ""}
                      onChange={(e) => updateRule(idx, "transformation", e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:border-blue-500 bg-white"
                    >
                      <option value="">Select...</option>
                      {TRANSFORM_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
              <button
                onClick={addRule}
                className="text-xs font-bold text-blue-600 hover:underline inline-block mt-2"
              >+ Add Mapping Rule</button>
            </div>
          );
        })()}

      </div>
    </div>
  );
};

// ─── Main Launch Builder canvas ───────────────────────────────────────────────
const LaunchBuilderCanvas = ({ initialTemplate, onBack }) => {
  const navigate = useNavigate();
  const reactFlowWrapper = useRef(null);

  // ── Restore persisted canvas state from sessionStorage ───────────────────
  const savedCanvas = (() => {
    try { return JSON.parse(sessionStorage.getItem("launchBuilderCanvas") || "null"); }
    catch { return null; }
  })();

  const [nodes, setNodes, onNodesChange] = useNodesState(savedCanvas?.nodes || []);
  const [edges, setEdges, onEdgesChange] = useEdgesState(savedCanvas?.edges || []);
  const [selectedNode, setSelectedNode] = useState(null);
  const [pipelineName, setPipelineName] = useState(savedCanvas?.pipelineName || "");
  const [pipelineDescription, setPipelineDescription] = useState(savedCanvas?.pipelineDescription || "");
  const [isSaving, setIsSaving] = useState(false);
  const [activeTemplate, setActiveTemplate] = useState(null);

  // Determine which templates to show based on the user's initial mode selection
  const showBroadcast   = !initialTemplate || initialTemplate.startsWith("fan_out");
  const showAggregate   = !initialTemplate || initialTemplate.startsWith("fan_in");
  const showSequential  = !initialTemplate || initialTemplate.startsWith("sequential");
  const [showPalette, setShowPalette] = useState(false);

  // ── Persist canvas state to sessionStorage whenever it changes ───────────
  useEffect(() => {
    sessionStorage.setItem("launchBuilderCanvas", JSON.stringify({
      nodes, edges, pipelineName, pipelineDescription,
    }));
  }, [nodes, edges, pipelineName, pipelineDescription]);

  // Auto-load the template chosen on the picker screen — skip if restoring
  useEffect(() => {
    if (initialTemplate && !savedCanvas) loadTemplate(initialTemplate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTemplate]);

  // ── Connect nodes ─────────────────────────────────────────────────────────
  const onConnect = useCallback(
    (params) =>
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            animated: false,
            style: { stroke: '#23346e', strokeWidth: 1.5 },
            markerEnd: { type: MarkerType.ArrowClosed, color: '#23346e' },
          },
          eds
        )
      ),
    [setEdges]
  );

  // ── Drag from palette ─────────────────────────────────────────────────────
  const onDragStart = (event, nodeType, label) => {
    event.dataTransfer.setData("application/reactflow-type", nodeType);
    event.dataTransfer.setData("application/reactflow-label", label);
    event.dataTransfer.effectAllowed = "move";
  };

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const nodeType = event.dataTransfer.getData("application/reactflow-type");
      const label = event.dataTransfer.getData("application/reactflow-label");
      if (!nodeType) return;

      const position = {
        x: event.clientX - reactFlowBounds.left - 72,
        y: event.clientY - reactFlowBounds.top - 30,
      };

      const newNode = {
        id: uuidv4(),
        type: nodeType,
        position,
        data: { label },
      };
      setNodes((nds) => nds.concat(newNode));
    },
    [setNodes]
  );

  // ── Load template ─────────────────────────────────────────────────────────
  const loadTemplate = (templateKey) => {
    const tpl = TEMPLATES[templateKey];
    if (!tpl) return;
    // Assign fresh UUIDs to avoid conflicts
    const idMap = {};
    const newNodes = tpl.nodes.map((n) => {
      const newId = uuidv4();
      idMap[n.id] = newId;
      return { ...n, id: newId };
    });
    const newEdges = tpl.edges.map((e) => ({
      ...e,
      id: uuidv4(),
      source: idMap[e.source],
      target: idMap[e.target],
    }));
    setNodes(newNodes);
    setEdges(newEdges);
    setActiveTemplate(templateKey);
    // Auto-select the first node so the right panel opens immediately
    setSelectedNode(newNodes[0] || null);
  };

  // ── Node click ────────────────────────────────────────────────────────────
  const onNodeClick = useCallback((_, node) => {
    setSelectedNode(node);
  }, []);

  const onNodeConfigChange = useCallback((updatedNode) => {
    setNodes((nds) =>
      nds.map((n) => (n.id === updatedNode.id ? updatedNode : n))
    );
    setSelectedNode(updatedNode);
  }, [setNodes]);

  // ── Save workflow graph ───────────────────────────────────────────────────
  const handleSave = async () => {
    if (!pipelineName || pipelineName.trim() === "") {
      toast.warning("Please provide an Integration Name before saving.");
      return;
    }

    if (!pipelineDescription || pipelineDescription.trim() === "") {
      toast.warning("Please provide a Description before saving.");
      return;
    }

    if (nodes.length === 0) {
      toast.warning("Add at least one node before saving.");
      return;
    }

    let validationError = "";
    const noResponseSchemaPlatforms = ["AWS SNS", "AWS SQS", "Azure Service Bus", "Salesforce Platform Event"];

    const invalidNodes = nodes.filter(n => {
      if (n.type === "trigger" || n.type === "connector") {
        if (!n.data.platform) {
          validationError = "Please select a platform for all Trigger and Connector nodes.";
          return true;
        }

        const schema = PLATFORM_FIELDS[n.type];
        const fields = schema?.fields?.[n.data.platform] || [];
        const config = n.data.config || {};

        for (const field of fields) {
          if (field.required && (!config[field.key] || String(config[field.key]).trim() === "")) {
            validationError = `Please provide "${field.label}" for ${n.data.platform} node.`;
            return true;
          }
        }

        // For trigger nodes (non-timer), require request schema before saving
        if (n.type === "trigger" && n.data.platform !== "Time Trigger") {
          const requestSchemaKey = fields.find(f => f.key === "requestPayloadSchema") ? "requestPayloadSchema" : "requestSchema";
          const hasRequestSchemaField = fields.some(f => f.key === requestSchemaKey);
          if (hasRequestSchemaField && (!config[requestSchemaKey] || String(config[requestSchemaKey]).trim() === "")) {
            validationError = `Please provide a Request Schema for the "${n.data.platform}" trigger before saving.`;
            return true;
          }

          // Require response schema for platforms that send back a response
          if (!noResponseSchemaPlatforms.includes(n.data.platform)) {
            const hasResponseSchemaField = fields.some(f => f.key === "responseSchema");
            if (hasResponseSchemaField && (!config.responseSchema || String(config.responseSchema).trim() === "")) {
              validationError = `Please provide a Response Schema for the "${n.data.platform}" trigger before saving.`;
              return true;
            }
          }
        }
      }
      return false;
    });

    if (invalidNodes.length > 0) {
      toast.error(validationError || "Please configure all required details before saving.");
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        name: pipelineName || "Untitled Workflow",
        description: pipelineDescription || "",
        graph_json: {
          nodes: nodes.map((n) => ({
            id: n.id,
            type: n.type,
            label: n.data.label,
            config: n.data,
            position_x: n.position.x,
            position_y: n.position.y,
          })),
          edges: edges.map((e) => ({
            id: e.id,
            source: e.source,
            target: e.target,
            condition: e.data?.condition || null,
            label: e.label || "",
          })),
        },
        graph_type: detectGraphType(nodes, edges),
      };

      await httpClient.post("/api/integrations/workflow-graphs/", payload);
      toast.success("Workflow saved successfully!");
      navigate(`/integrations/listing`);
    } catch (err) {
      toast.error(parseError(err));
    } finally {
      setIsSaving(false);
    }
  };

  const detectGraphType = (nds, eds) => {
    const hasAggregator = nds.some((n) => n.type === "aggregator");
    const hasRouter = nds.some((n) => n.type === "router");
    const triggerNode = nds.find((n) => n.type === "trigger");
    if (!triggerNode) return "fan_in";
    const fanOutBranches = eds.filter((e) => e.source === triggerNode.id).length;
    if (hasAggregator) return "fan_in";
    if (hasRouter) return "conditional";
    if (fanOutBranches > 1) return "fan_out";
    return "linear";
  };

  return (
    <div className="absolute inset-0 flex flex-col bg-gray-50 full-bleed">

      {/* ── Body ────────────────────────────────────────────────────────────── */}
      <div className="flex flex-1 overflow-hidden">
        {/* Palette sidebar */}
        <div
          className={`fixed lg:relative inset-y-0 left-0 z-[30] w-56 flex-shrink-0 border-r border-gray-200 bg-white overflow-y-auto transform transition-transform duration-300 ease-in-out ${showPalette ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
            }`}
        >
          {/* Integration Details: Pipeline Name & Desc */}
          <div className="p-3 border-b border-gray-200 bg-gray-50 flex flex-col relative">
            <button onClick={() => setShowPalette(false)} className="lg:hidden absolute top-2 right-3 text-gray-400 hover:text-gray-600 text-xl font-bold">×</button>
            <label className="text-xs font-bold text-black mb-2">Integration Details</label>

            <div className="mb-2">
              <label className="text-[10px] font-bold text-gray-700 block mb-0.5">Name</label>
              <input
                type="text"
                value={pipelineName}
                onChange={e => setPipelineName(e.target.value)}
                placeholder="Integration Name"
                className="w-full font-bold text-gray-800 text-[11px] focus:outline-none border border-gray-300 rounded pt-1 pb-1 px-1.5 bg-white"
              />
            </div>
            <div>
              <label className="text-[10px] font-bold text-gray-700 block mb-0.5">Description</label>
              <textarea
                value={pipelineDescription}
                onChange={e => setPipelineDescription(e.target.value)}
                placeholder="Add a description..."
                rows={2}
                className="w-full text-[10px] text-gray-600 focus:outline-none border border-gray-300 rounded pt-1 pb-1 px-1.5 bg-white resize-none"
              />
            </div>
          </div>

          <div className="px-3 pt-3 pb-1 flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-gray-900">Node Palette</p>
              <p className="text-xs text-gray-400 mt-0.5">Drag onto canvas</p>
            </div>
          </div>
          <div className="p-3 space-y-2">
            {NODE_PALETTE.map((item) => {
              const colors = COLOR_MAP[item.color];
              return (
                <div
                  key={item.type}
                  draggable
                  onDragStart={(e) => {
                    onDragStart(e, item.type, item.label);
                    // Close sidebar on mobile after starting drag to see canvas
                    if (window.innerWidth < 1024) setShowPalette(false);
                  }}
                  className={`flex items-center gap-2 rounded-lg border border-dashed p-2 cursor-grab active:cursor-grabbing transition-all ${colors.drag}`}
                >
                  <span className={`rounded-md p-1 ${colors.pill}`}>{React.cloneElement(item.icon, { className: 'w-3 h-3' })}</span>
                  <div>
                    <div className="text-[11px] font-bold text-gray-800 leading-tight">{item.label}</div>
                    <div className="text-[9px] text-gray-500 leading-tight">{item.description}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pattern quick-load buttons */}
          <div className="px-3 pt-3 pb-4 border-t border-gray-100 mt-2">
            <p className="text-[11px] font-bold text-gray-900 mb-3">Load Pattern</p>

            {/* Broadcast group */}
            {showBroadcast && (
              <div className="mb-3">
                {/* Removed Outbound Broadcast Protocol header */}
                <div className="space-y-1.5">
                  {[
                    {
                      key: "fan_out",
                      label: "Direct Broadcast",
                      sublabel: "One platform fires & sends data to all destinations",
                    },
                    {
                      key: "fan_out_b",
                      label: "Event-Triggered Broadcast",
                      sublabel: "An event fires, reads data from a source, then sends to all",
                    },
                  ].map((p) => (
                    <button
                      key={p.key}
                      onClick={() => loadTemplate(p.key)}
                      className={`w-full text-left rounded-lg p-2 transition-all border ${activeTemplate === p.key
                        ? "bg-purple-50 border-purple-400 shadow-sm"
                        : "bg-white border-gray-200 hover:border-purple-200 hover:bg-purple-50/40"
                        }`}
                    >
                      <div className={`text-[11px] font-bold ${activeTemplate === p.key ? "text-purple-700" : "text-gray-700"}`}>
                        {p.label}
                      </div>
                      <div className="text-[9px] text-gray-500 leading-tight">{p.sublabel}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Aggregate group */}
            {showAggregate && (
              <div className="mt-3">
                <div className="space-y-1.5">
                  {[
                    {
                      key: "fan_in",
                      label: "Merge from Multiple Sources",
                      sublabel: "Each source triggers and sends its own data into one destination",
                    },
                    {
                      key: "fan_in_b",
                      label: "Fetch & Merge",
                      sublabel: "Each input leg has a separate event trigger and data reader",
                    },
                  ].map((p) => (
                    <button
                      key={p.key}
                      onClick={() => loadTemplate(p.key)}
                      className={`w-full text-left rounded-lg p-2 transition-all border ${activeTemplate === p.key
                        ? "bg-green-50 border-green-400 shadow-sm"
                        : "bg-white border-gray-200 hover:border-green-200 hover:bg-green-50/40"
                        }`}
                    >
                      <div className={`text-[11px] font-bold ${activeTemplate === p.key ? "text-green-700" : "text-gray-700"}`}>
                        {p.label}
                      </div>
                      <div className="text-[9px] text-gray-500 leading-tight">{p.sublabel}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Sequential group */}
            {showSequential && (
              <div className="mt-3">
                <div className="space-y-1.5">
                  {[
                    {
                      key: "sequential",
                      label: "3-Step Chain  (A → B → C)",
                      sublabel: "Trigger fires data through one middle step to a destination",
                    },
                    {
                      key: "sequential_4",
                      label: "4-Step Chain  (A → B → C → D)",
                      sublabel: "Add an extra processing step before the final destination",
                    },
                  ].map((p) => (
                    <button
                      key={p.key}
                      onClick={() => loadTemplate(p.key)}
                      className={`w-full text-left rounded-lg p-2 transition-all border ${activeTemplate === p.key
                        ? "bg-indigo-50 border-indigo-400 shadow-sm"
                        : "bg-white border-gray-200 hover:border-indigo-200 hover:bg-indigo-50/40"
                        }`}
                    >
                      <div className={`text-[11px] font-bold ${activeTemplate === p.key ? "text-indigo-700" : "text-gray-700"}`}>
                        {p.label}
                      </div>
                      <div className="text-[9px] text-gray-500 leading-tight">{p.sublabel}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

          </div>
        </div>

        {/* Mobile Backdrop */}
        {showPalette && (
          <div
            className="fixed inset-0 bg-black/20 z-[25] lg:hidden"
            onClick={() => setShowPalette(false)}
          />
        )}

        {/* Canvas */}
        <div className="relative flex-1" ref={reactFlowWrapper}>
          {/* Floating toggle for palette on mobile */}
          {!showPalette && (
            <button
              onClick={() => setShowPalette(true)}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white border border-gray-200 border-l-0 rounded-r-xl px-3 py-4 text-gray-500 hover:text-blue-600 hover:border-blue-300 transition-all shadow-md lg:hidden"
              title="Show Palette"
            >
              <div className="flex flex-col items-center gap-1">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <span className="[writing-mode:vertical-lr] text-[10px] uppercase font-bold tracking-widest mt-1">Nodes</span>
              </div>
            </button>
          )}
          {/* ← Back — top-left of canvas */}
          <button
            onClick={() => onBack ? onBack() : navigate(-1)}
            className="absolute top-3 left-3 z-10 flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700 transition-all shadow-md"
          >
            ← Back
          </button>

          <div className="absolute top-3 right-3 z-10 flex items-center gap-2">
            {/* Integrations shortcut */}
            <button
              onClick={() => navigate("/integrations/listing")}
              className="flex items-center gap-2 rounded-full bg-[#f0f7ff] border border-[#cce3ff] px-5 py-2 text-sm font-bold text-[#1e3a8a] hover:bg-[#e0efff] hover:border-[#b8d9ff] transition-all shadow-sm active:scale-95"
            >
              Integrations
            </button>
            {/* Save Workflow — top-right of canvas */}
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center gap-2 rounded-full bg-blue-600 px-5 py-2 text-sm font-bold text-white hover:bg-blue-700 transition-all shadow-md disabled:opacity-60"
            >
              {isSaving ? "Saving..." : "Save Workflow"}
            </button>
          </div>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onNodeClick={onNodeClick}
            nodeTypes={PIPELINE_NODE_TYPES}
            fitView
            deleteKeyCode="Delete"
          >
            <Background variant="dots" gap={16} size={1} color="#e5e7eb" />
            <Controls />
          </ReactFlow>

          {/* Empty canvas hint */}
          {nodes.length === 0 && (
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-4 text-gray-400">
              <svg className="w-16 h-16 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1}
                  d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
              </svg>
              <div className="text-center">
                <p className="text-lg font-semibold">Drag nodes from the palette</p>
                <p className="text-sm">or use a template above to get started</p>
              </div>
            </div>
          )}

          {/* Node config panel — shows only when a node is selected */}
          {selectedNode && (
            <NodeConfigPanel
              node={selectedNode}
              onChange={onNodeConfigChange}
              onClose={() => setSelectedNode(null)}
              onDelete={(id) => {
                setNodes((nds) => nds.filter((n) => n.id !== id));
                setEdges((eds) => eds.filter((e) => e.source !== id && e.target !== id));
                setSelectedNode(null);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Mode picker cards ────────────────────────────────────────────────────────
const MODES = [
  {
    key: "agent",
    label: "Launch Agent",
    sub: "Use an AI agent to power your integration",
    badge: "AI-powered",
    badgeColor: "bg-blue-100 text-blue-700 border-blue-200",
    border: "border-blue-400",
    bg: "hover:bg-blue-50",
    icon: (
      <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    ),
    iconColor: "text-blue-500",
    desc: "",
  },
  {
    key: "one_to_one",
    label: "1-to-1 Integration",
    sub: "Connect one system to another system",
    badge: "Simple",
    badgeColor: "bg-purple-100 text-purple-700 border-purple-200",
    border: "border-purple-400",
    bg: "hover:bg-purple-50",
    icon: (
      <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
          d="M13 7l5 5m0 0l-5 5m5-5H6" />
      </svg>
    ),
    iconColor: "text-purple-500",
    desc: "",
  },
  {
    key: "fan_out",
    label: "Broadcast to Multiple",
    sub: "Send data from one system to many systems",
    badge: "Multi-destination",
    badgeColor: "bg-green-100 text-green-700 border-green-200",
    border: "border-green-400",
    bg: "hover:bg-green-50",
    icon: (
      <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
          d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    iconColor: "text-green-500",
    desc: "",
  },
  {
    key: "fan_in",
    label: "Aggregate from Multiple",
    sub: "Collect data from many systems into one system",
    badge: "Multi-source",
    badgeColor: "bg-orange-100 text-orange-700 border-orange-200",
    border: "border-orange-400",
    bg: "hover:bg-orange-50",
    icon: (
      <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
          d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
      </svg>
    ),
    iconColor: "text-orange-500",
    desc: "",
  },
  {
    key: "sequential",
    label: "Sequential Integration",
    sub: "Chain systems step by step with sequential data flow",
    badge: "Multi-step",
    badgeColor: "bg-indigo-100 text-indigo-700 border-indigo-200",
    border: "border-indigo-400",
    bg: "hover:bg-indigo-50",
    icon: (
      <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
          d="M17 8l4 4m0 0l-4 4m4-4H3" />
      </svg>
    ),
    iconColor: "text-indigo-500",
    desc: "",
  },
];

// ─── Mode picker ──────────────────────────────────────────────────────────────
const ModePicker = ({ onSelect }) => {
  const navigate = useNavigate();
  return (
    <div className="h-full bg-gray-50 flex flex-col">
      <div className="px-6 pt-4 flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700 transition-all"
        >
          ← Back
        </button>

        <button
          onClick={() => navigate("/integrations/listing")}
          className="flex items-center gap-2 rounded-full bg-blue-600 px-5 py-2 text-sm font-bold text-white hover:bg-blue-700 transition-all shadow-sm active:scale-95"
        >
          Integrations
          <svg className="w-4 h-4 ml-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="12 5 19 12 12 19" />
          </svg>
        </button>
      </div>
      <div className="flex-1 flex items-start justify-center px-8 pt-6 pb-8">
        <div className="w-full max-w-2xl">
          <h2 className="text-center text-xl font-semibold text-black mb-1">
            How would you like to integrate?
          </h2>
          <p className="text-center text-xs text-black mb-8">
            Select the data flow pattern that matches your use case.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 auto-rows-fr">
            {MODES.map((mode) => (
              <button
                key={mode.key}
                onClick={() => onSelect(mode.key)}
                className={`group flex items-center h-full gap-4 rounded-xl border-2 ${mode.border} bg-white px-5 py-4 text-left shadow-sm transition-all duration-300 ${mode.bg} hover:shadow-xl active:scale-95`}
              >
                <div className={`shrink-0 p-1.5 rounded-lg bg-gray-50 ${mode.iconColor || "text-gray-500"}`}>
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    {mode.icon.props.children}
                  </svg>
                </div>
                <div className="min-w-0 flex-1">
                  <span className="block font-bold text-black text-base leading-snug tracking-tight font-outfit">{mode.label}</span>
                  <p className="text-xs text-gray-600 leading-relaxed mt-0.5 font-outfit">{mode.sub}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Top-level router ─────────────────────────────────────────────────────────
const LaunchBuilder = () => {
  const navigate = useNavigate();
  const [selectedMode, setSelectedMode] = useState(() => sessionStorage.getItem("launchBuilderMode") || null);

  const handleModeSelect = (mode) => {
    if (mode === "one_to_one") {
      // Route directly to the existing standard integration editor
      const newId = generateGuid();
      navigate(`/integrations/${newId}/overview`);
      return;
    }
    if (mode === "agent") {
      navigate("/agent");
      return;
    }
    sessionStorage.setItem("launchBuilderMode", mode);
    setSelectedMode(mode);
  };

  if (!selectedMode) {
    return <ModePicker onSelect={handleModeSelect} />;
  }

  return (
    <ReactFlowProvider>
      <LaunchBuilderCanvas initialTemplate={selectedMode} onBack={() => {
        sessionStorage.removeItem("launchBuilderMode");
        sessionStorage.removeItem("launchBuilderCanvas");
        setSelectedMode(null);
      }} />
    </ReactFlowProvider>
  );
};

export default LaunchBuilder;
